import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Smartphone, CreditCard, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import paymentAPI from './paymentApi';
import MpesaModal from './MpesaModal';

const CheckoutPayment = ({ 
  orderId, 
  amount, 
  currency = 'KES',
  customerEmail,
  onPaymentSuccess,
  onPaymentCancel 
}) => {
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [loading, setLoading] = useState(false);
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const data = await paymentAPI.getMethods();
      setPaymentMethods(data.methods || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      switch (paymentMethod) {
        case 'mpesa':
          setShowMpesaModal(true);
          break;
          
        case 'stripe':
          const stripeResponse = await paymentAPI.createStripeSession(
            orderId, 
            amount,
            currency.toLowerCase(),
            customerEmail
          );
          if (stripeResponse.session_url) {
            window.location.href = stripeResponse.session_url;
          }
          break;
          
        case 'paypal':
          const paypalResponse = await paymentAPI.createPayPalOrder(
            orderId,
            amount,
            'USD'
          );
          if (paypalResponse.approval_url) {
            window.location.href = paypalResponse.approval_url;
          }
          break;
          
        case 'skrill':
          const skrillResponse = await paymentAPI.createSkrillSession(
            orderId,
            amount,
            'USD',
            customerEmail
          );
          if (skrillResponse.redirect_url) {
            window.location.href = skrillResponse.redirect_url;
          }
          break;
          
        default:
          toast.error('Invalid payment method');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment initiation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMpesaCancel = () => {
    setShowMpesaModal(false);
    onPaymentCancel?.();
  };

  return (
    <>
      <Card className="luxury-card">
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>Choose your preferred payment option</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
            {/* M-Pesa */}
            <div className={`flex items-center space-x-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${paymentMethod === 'mpesa' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'}`}>
              <RadioGroupItem value="mpesa" id="mpesa" />
              <Label htmlFor="mpesa" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold">M-Pesa</p>
                    <p className="text-sm text-muted-foreground">Pay with M-Pesa mobile money</p>
                  </div>
                </div>
              </Label>
              <span className="text-sm font-medium text-green-600">Recommended</span>
            </div>

            {/* Card (Stripe) */}
            <div className={`flex items-center space-x-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${paymentMethod === 'stripe' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'}`}>
              <RadioGroupItem value="stripe" id="stripe" />
              <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Card Payment</p>
                    <p className="text-sm text-muted-foreground">Visa, Mastercard, Amex</p>
                  </div>
                </div>
              </Label>
            </div>

            {/* PayPal */}
            <div className={`flex items-center space-x-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${paymentMethod === 'paypal' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'}`}>
              <RadioGroupItem value="paypal" id="paypal" />
              <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-indigo-600 font-bold text-sm">PP</span>
                  </div>
                  <div>
                    <p className="font-semibold">PayPal</p>
                    <p className="text-sm text-muted-foreground">Pay with PayPal account</p>
                  </div>
                </div>
              </Label>
            </div>

            {/* Skrill */}
            <div className={`flex items-center space-x-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${paymentMethod === 'skrill' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'}`}>
              <RadioGroupItem value="skrill" id="skrill" />
              <Label htmlFor="skrill" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Skrill</p>
                    <p className="text-sm text-muted-foreground">Pay with Skrill wallet</p>
                  </div>
                </div>
              </Label>
            </div>
          </RadioGroup>

          {/* Order Summary */}
          <div className="bg-secondary/30 p-4 rounded-xl">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Order Total</span>
              <span className="text-2xl font-bold font-mono">
                {currency} {amount.toLocaleString()}
              </span>
            </div>
          </div>

          <Button 
            onClick={handlePayment} 
            className="w-full btn-primary" 
            size="lg"
            disabled={loading}
          >
            {loading ? 'Processing...' : `Pay ${currency} ${amount.toLocaleString()}`}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Your payment is secured with 256-bit SSL encryption
          </p>
        </CardContent>
      </Card>

      {/* M-Pesa Modal */}
      <MpesaModal
        isOpen={showMpesaModal}
        onClose={handleMpesaCancel}
        orderId={orderId}
        amount={amount}
        userEmail={customerEmail}
      />
    </>
  );
};

export default CheckoutPayment;
